import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { LuPencil, LuShuffle, LuTrash2, LuTriangleAlert } from "react-icons/lu";
import {
  Button,
  Dialog,
  Field,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { FieldSkeleton } from "../components/LoadingSkeletons";
import { MultiSelectTags } from "../components/MultiSelectTags";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { readableError } from "@/lib/errors";
import type { PersonActivityRow, PersonOption } from "./ActivityAdminShared";

/**
 * What an activity admin may do to somebody else's copy.
 *
 * Model §9 makes this an ordinary capability rather than a repair mechanism:
 * reviewers are stated explicitly and there is no automatic fallback to "the
 * supervisor", so an admin has to be able to act on any assignment. Until now
 * these five mutations existed and were reachable only by calling them, which
 * made the capability real in the schema and imaginary in practice.
 *
 * Every one of them demands a reason, and the reason is written to the copy's
 * own history. That is the whole point of the local history (§9): "why is my
 * deadline different" is asked of this module specifically, and an act with no
 * stated reason cannot answer it.
 */
export type AssignmentAdminAction = "edit" | "status" | "reassign" | "delete";

/**
 * The actions on one row of the assignments table, as icon buttons -- the
 * catalogue's pattern -- rather than behind a "⋮" menu: each is one click,
 * and its name is on hover.
 *
 * The dialogs they open are not here. Mounted per row -- closed, but with
 * their contents, including a person picker built over the whole directory --
 * four hundred rows froze the page for nine seconds before it could be used.
 * The page holds one set (`AssignmentAdminDialogs`) and mounts the one asked
 * for.
 */
export function AssignmentAdminActions({
  onAction,
}: {
  onAction: (action: AssignmentAdminAction) => void;
}) {
  return (
    <HStack justify="flex-end" gap={1}>
      <IconButton aria-label="Edit dates and wording" title="Edit dates and wording" size="xs" variant="ghost" onClick={() => onAction("edit")}>
        <LuPencil />
      </IconButton>
      <IconButton aria-label="Override the status" title="Override the status" size="xs" variant="ghost" onClick={() => onAction("status")}>
        <LuTriangleAlert />
      </IconButton>
      <IconButton aria-label="Reassign to somebody else" title="Reassign to somebody else" size="xs" variant="ghost" onClick={() => onAction("reassign")}>
        <LuShuffle />
      </IconButton>
      {/* Deletion is not an admin act in the §9 sense -- it leaves no
          history, because there is nothing left to hold it. It is for a
          genuine mistake, and archiving is the ordinary exit. */}
      <IconButton aria-label="Delete outright" title="Delete outright" size="xs" variant="ghost" colorPalette="red" onClick={() => onAction("delete")}>
        <LuTrash2 />
      </IconButton>
    </HStack>
  );
}

/**
 * The table's one set of admin dialogs: whichever was chosen, for the row it
 * was chosen on.
 *
 * A dialog here is mounted CLOSED and opened on the next render, never
 * mounted open. Mounted open, it locked the page as a modal does -- body
 * `overflow: hidden; pointer-events: none` -- and never unlocked it on
 * closing: its content went, the locks stayed, and nothing on the page could
 * be clicked again. The Delete dialog beside it, always mounted and switched
 * from closed to open, never did this; this takes the same path.
 *
 * The last one chosen stays mounted after it closes, so it closes as a
 * dialog does rather than vanishing mid-way. Keyed by row and action, so
 * choosing another starts it fresh.
 */
export function AssignmentAdminDialogs({
  target,
  persons,
  onClose,
}: {
  target: { row: PersonActivityRow; action: Exclude<AssignmentAdminAction, "delete"> } | null;
  persons: PersonOption[] | undefined;
  onClose: () => void;
}) {
  const [last, setLast] = useState<typeof target>(null);
  const [open, setOpen] = useState(false);
  if (target !== null && target !== last) {
    setLast(target);
    setOpen(false);
  }
  useEffect(() => {
    setOpen(target !== null && target === last);
  }, [target, last]);
  if (last === null) {
    return null;
  }
  const key = `${last.row._id}:${last.action}`;
  switch (last.action) {
    case "edit":
      return <EditDialog key={key} row={last.row} open={open} onClose={onClose} />;
    case "status":
      return <StatusDialog key={key} row={last.row} open={open} onClose={onClose} />;
    case "reassign":
      return <ReassignDialog key={key} row={last.row} persons={persons} open={open} onClose={onClose} />;
  }
}

/** The reason field, identical on all three, because the rule is identical. */
function ReasonField({
  value,
  onChange,
  hint,
}: {
  value: string;
  onChange: (next: string) => void;
  hint: string;
}) {
  return (
    <Field.Root required>
      <Field.Label>Why</Field.Label>
      <Textarea rows={2} value={value} onChange={(event) => onChange(event.target.value)} />
      <Field.HelperText>{hint}</Field.HelperText>
    </Field.Root>
  );
}

function EditDialog({
  row,
  open,
  onClose,
}: {
  row: PersonActivityRow;
  open: boolean;
  onClose: () => void;
}) {
  const adminEdit = useMutation(api.personActivities.adminEdit);
  const [draft, setDraft] = useState(() => fromRow(row));
  const [saving, setSaving] = useState(false);

  // Re-seed when the dialog opens on a different row, without an effect: a
  // draft that survived a close would reappear over the next one.
  const [lastId, setLastId] = useState(String(row._id));
  if (String(row._id) !== lastId) {
    setLastId(String(row._id));
    setDraft(fromRow(row));
  }

  async function save() {
    setSaving(true);
    try {
      await adminEdit({
        personActivityId: row._id as Id<"personActivities">,
        titleSnapshot: draft.title,
        descriptionSnapshot: draft.description,
        required: draft.required,
        recommendedStartDay: draft.recommendedStartDay === "" ? null : draft.recommendedStartDay,
        opensOnDay: draft.opensOnDay === "" ? null : draft.opensOnDay,
        dueDay: draft.dueDay === "" ? null : draft.dueDay,
        reason: draft.reason,
      });
      toastSuccess("Edited", `${row.personName} has been told.`);
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The edit could not be saved");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      title={`Edit ${row.personName}'s copy`}
      confirmLabel="Save the edit"
      disabled={draft.reason.trim() === "" || draft.title.trim() === ""}
      saving={saving}
      onConfirm={() => void save()}
      size="lg"
    >
      <Text fontSize="sm" color="fg.muted">
        This changes one person's copy and nothing else. The catalogue entry is untouched, and so
        is everybody else holding this activity.
      </Text>

      <Field.Root required>
        <Field.Label>Title</Field.Label>
        <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
      </Field.Root>
      <Field.Root>
        <Field.Label>Description</Field.Label>
        <Textarea
          rows={2}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </Field.Root>

      <HStack gap={3} align="start" wrap="wrap">
        <Field.Root maxW="12rem">
          <Field.Label>Suggested start</Field.Label>
          <Input
            type="date"
            value={draft.recommendedStartDay}
            onChange={(e) => setDraft({ ...draft, recommendedStartDay: e.target.value })}
          />
        </Field.Root>
        <Field.Root maxW="12rem">
          <Field.Label>Opens on</Field.Label>
          <Input
            type="date"
            value={draft.opensOnDay}
            onChange={(e) => setDraft({ ...draft, opensOnDay: e.target.value })}
          />
        </Field.Root>
        <Field.Root maxW="12rem">
          <Field.Label>Due</Field.Label>
          <Input
            type="date"
            value={draft.dueDay}
            onChange={(e) => setDraft({ ...draft, dueDay: e.target.value })}
          />
        </Field.Root>
      </HStack>

      <Switch.Root
        colorPalette="blue"
        checked={draft.required}
        onCheckedChange={(e) => setDraft({ ...draft, required: e.checked === true })}
      >
        <Switch.HiddenInput />
        <Switch.Control />
        <Switch.Label fontSize="sm">
          Required — {draft.required
            ? "counts towards their obligations and can go overdue"
            : "encouraged: it never goes overdue and blocks nothing"}
        </Switch.Label>
      </Switch.Root>

      <ReasonField
        value={draft.reason}
        onChange={(reason) => setDraft({ ...draft, reason })}
        hint="Written to this copy's history, where the person can read it."
      />
    </AdminDialog>
  );
}

function fromRow(row: PersonActivityRow) {
  return {
    title: row.titleSnapshot,
    description: row.descriptionSnapshot,
    required: row.required,
    recommendedStartDay: row.recommendedStartDay ?? "",
    opensOnDay: row.opensOnDay ?? "",
    dueDay: row.dueDay ?? "",
    reason: "",
  };
}

/**
 * The release valve of model §9: an admin may put a copy into any state.
 *
 * Deliberately unrestricted, because the cases that need it cannot be
 * enumerated -- a reviewer who has left, an activity completed outside the
 * system, a mistake nobody can unpick. What keeps it honest is the reason and
 * the history entry, not a shorter list of permitted states.
 */
function StatusDialog({
  row,
  open,
  onClose,
}: {
  row: PersonActivityRow;
  open: boolean;
  onClose: () => void;
}) {
  const adminSetStatus = useMutation(api.personActivities.adminSetStatus);
  const [status, setStatus] = useState(row.status as string);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await adminSetStatus({
        personActivityId: row._id as Id<"personActivities">,
        status: status as never,
        reason,
      });
      toastSuccess("Status overridden", `${row.personName} has been told.`);
      setReason("");
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The status could not be changed");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      title={`Override the status of ${row.personName}'s copy`}
      confirmLabel="Override it"
      disabled={reason.trim() === ""}
      saving={saving}
      onConfirm={() => void save()}
    >
      <Text fontSize="sm" color="fg.muted">
        Status is normally derived from the work — what has been submitted, signed, or is waiting.
        Setting it by hand overrides that derivation until something recomputes it.
      </Text>

      <Field.Root>
        <Field.Label>New status</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="not_started">Not started</option>
            <option value="in_progress">In progress</option>
            <option value="awaiting_review">Waiting on someone else</option>
            <option value="needs_revision">Needs revision</option>
            <option value="blocked">Not yet open</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="archived">Archived</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      <ReasonField
        value={reason}
        onChange={setReason}
        hint="Written to this copy's history. It is the only account of why the status is what it is."
      />
    </AdminDialog>
  );
}

function ReassignDialog({
  row,
  persons,
  open,
  onClose,
}: {
  row: PersonActivityRow;
  persons: PersonOption[] | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const adminReassign = useMutation(api.personActivities.adminReassign);
  const [toPersonId, setToPersonId] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const chosen = toPersonId[0];

  async function save() {
    if (chosen === undefined) {
      return;
    }
    setSaving(true);
    try {
      await adminReassign({
        personActivityId: row._id as Id<"personActivities">,
        toPersonId: chosen as Id<"persons">,
        reason,
      });
      toastSuccess("Reassigned", "Both people have been told.");
      setToPersonId([]);
      setReason("");
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "It could not be reassigned");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      title={`Reassign ${row.titleSnapshot}`}
      confirmLabel="Reassign it"
      disabled={chosen === undefined || reason.trim() === ""}
      saving={saving}
      onConfirm={() => void save()}
    >
      <Text fontSize="sm" color="fg.muted">
        The copy moves to somebody else, with everything on it: its answers, its files, its
        reviews and its history. It is the same work under new ownership, not a fresh assignment.
      </Text>

      {persons === undefined ? (
        <FieldSkeleton label="Loading people" height="16" />
      ) : (
        <MultiSelectTags
          label={`From ${row.personName} to`}
          placeholder="Search by name or address…"
          emptyMessage="Nobody matches that."
          helperText="One person. Choosing another replaces the choice."
          options={persons
            .filter((person) => String(person._id) !== String(row.personId))
            .map((person) => ({
              value: String(person._id),
              label: person.fullName,
              hint: person.email ?? undefined,
            }))}
          selected={toPersonId}
          onChange={(next) => setToPersonId(next.length === 0 ? [] : [next[next.length - 1]!])}
        />
      )}

      <ReasonField
        value={reason}
        onChange={setReason}
        hint="Written to the copy's history, and both people are told."
      />
    </AdminDialog>
  );
}

/** The shell all three share: same shape, same reason field, same buttons. */
function AdminDialog({
  open,
  onClose,
  title,
  confirmLabel,
  disabled,
  saving,
  onConfirm,
  size = "md",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  confirmLabel: string;
  disabled: boolean;
  saving: boolean;
  onConfirm: () => void;
  size?: "md" | "lg";
  children: React.ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }} size={size}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header><Dialog.Title>{title}</Dialog.Title></Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>{children}</Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button colorPalette="blue" loading={saving} disabled={disabled} onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

/**
 * The two acts that take a PERSON rather than a copy.
 *
 * They belong together and away from the row menu, because their subject is
 * different: "shift everything still outstanding" and "archive everything
 * open" are answers to something happening to the person — a leave of
 * absence measured after the fact, a departure — not to anything about one
 * activity.
 *
 * Not on the programme page, which is the obvious-looking home: that page is
 * scoped to an enrollment and returns nothing for the postdocs and staff who
 * also hold activities. The admin's own workspace is where every person's
 * work is already listed.
 */
export function PersonAdminDialog({
  open,
  onClose,
  persons,
  rows,
}: {
  open: boolean;
  onClose: () => void;
  persons: PersonOption[] | undefined;
  rows: PersonActivityRow[] | undefined;
}) {
  const shiftDates = useMutation(api.personActivities.adminShiftRemainingDates);
  const archivePerson = useMutation(api.personActivities.adminArchivePerson);

  const [selected, setSelected] = useState<string[]>([]);
  const [act, setAct] = useState("shift");
  const [days, setDays] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const personId = selected[0];

  // Counted from what the page already holds, so the dialog can say what it
  // is about to touch rather than reporting it afterwards. Both acts skip
  // work that is finished, archived or cancelled, and this counts the same
  // way so the number is the one that will move.
  const theirs = (rows ?? []).filter((row) => String(row.personId) === personId);
  const open_ = theirs.filter((row) =>
    row.status !== "completed" && row.status !== "archived" && row.status !== "cancelled");

  async function run() {
    if (personId === undefined) {
      return;
    }
    setSaving(true);
    try {
      if (act === "shift") {
        const result = await shiftDates({
          personId: personId as Id<"persons">,
          days: Number(days),
          reason,
        });
        toastSuccess(`${result.shifted} moved`, "They have been told.");
      } else {
        const result = await archivePerson({ personId: personId as Id<"persons">, reason });
        toastSuccess(`${result.archived} archived`, "Completed work was left as it is.");
      }
      setSelected([]);
      setDays("");
      setReason("");
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "That could not be done");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  const shiftInvalid = act === "shift"
    && (days.trim() === "" || Number(days) === 0 || !Number.isFinite(Number(days)));

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      title="Act on everything one person holds"
      confirmLabel={act === "shift" ? "Move the dates" : "Archive their open work"}
      disabled={personId === undefined || reason.trim() === "" || shiftInvalid}
      saving={saving}
      onConfirm={() => void run()}
      size="lg"
    >
      {persons === undefined ? (
        <FieldSkeleton label="Loading people" height="16" />
      ) : (
        <MultiSelectTags
          label="Whose work"
          placeholder="Search by name or address…"
          emptyMessage="Nobody matches that."
          options={persons.map((person) => ({
            value: String(person._id),
            label: person.fullName,
            hint: person.email ?? undefined,
          }))}
          selected={selected}
          onChange={(next) => setSelected(next.length === 0 ? [] : [next[next.length - 1]!])}
        />
      )}

      <Field.Root>
        <Field.Label>What to do</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field value={act} onChange={(e) => setAct(e.target.value)}>
            <option value="shift">Move every remaining date</option>
            <option value="archive">Archive everything still open</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        <Field.HelperText>
          {act === "shift"
            ? "For a leave of absence measured after the fact, or a deadline that moved for one person. Finished work stays put, and a step already signed off keeps the deadline it met."
            : "For a departure or a discontinued enrolment. Archived work leaves every queue and stays on their record; completed work is untouched."}
        </Field.HelperText>
      </Field.Root>

      {act === "shift" ? (
        <Field.Root required invalid={shiftInvalid && days.trim() !== ""}>
          <Field.Label>By how many days</Field.Label>
          <Input
            type="number"
            maxW="10rem"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            placeholder="e.g. 90"
          />
          <Field.HelperText>Negative to pull dates earlier. Zero changes nothing and is refused.</Field.HelperText>
        </Field.Root>
      ) : null}

      {personId === undefined ? null : (
        <Text fontSize="sm" color={open_.length === 0 ? "fg.muted" : "fg"}>
          {open_.length === 0
            ? "They hold nothing that is still open, so this would change nothing."
            : `This touches ${open_.length} of their ${theirs.length} ${theirs.length === 1 ? "activity" : "activities"}.`}
        </Text>
      )}

      <ReasonField
        value={reason}
        onChange={setReason}
        hint="Written to the history of every copy it touches."
      />
    </AdminDialog>
  );
}
